/* eslint-disable no-useless-escape */
import { AxiosRequestConfig, AxiosResponse } from 'axios'
import { ContractEdit } from 'contexts/contract/interface'
import { environments } from 'environments/environments'
import {
    ContractSearchRequestDTO,
    ContractSearchResponseDTO,
    ContractsQueryDTO,
    ContractsResponseDTO,
    HistorySearchRequestDTO,
    HistorySearchResponseDTO,
    LoteQueryDTO,
    LoteResponseDTO
} from 'models'
import { ContractAnnexRequestDTO } from 'models/ContractAnnexRequestDTO'
import { ContractRequestDTO } from 'models/ContractRequestDTO'
import { ContractRequestUpdateDTO } from 'models/ContractRequestUpdateDTO'
import { ExternalAdditiveRequestDTO } from 'models/ExternalAdditiveRequestDTO'
import { ContractResponseUpdateDTO } from 'models/ContractResponseUpdateDTO'
import { FinalResponse } from 'pages/Contracts/Components/StepsForm/interface'
import { ExportContractsParams } from 'pages/Contracts/interfaces'
import { QueryVehicles } from 'pages/Vehicles/types'
import { removeEmptyValues } from 'utils/removeEmptyValues'
import api from './api'

const { ENDPOINT_CONTRACTS, ENDPOINT_CONTRACTS_V2, ENDPOINT_API_INSTITUTIONS, ENDPOINT_API_DETRANS } =
    environments

interface PayloadQRCodeContract {
    hashVersao: string
    id: string
}

interface ParamasChangeStatus {
    id: string
    body: {
        motivo: string
        tipoOperacao: number
    }
}

const editContract = async (
    id: string,
    data: ContractRequestUpdateDTO,
    type: ContractEdit
): Promise<AxiosResponse<FinalResponse>> => {
    switch (type) {
        case 'UPDATE':
            return await api.put(`${ENDPOINT_CONTRACTS}/contratos/${id}`, data)
        case 'ADDITIVE':
            return await api.post(
                `${ENDPOINT_CONTRACTS}/contratos/${id}/aditivos`,
                data
            )
        case 'EDITADDITIVE':
            return await api.put(
                `${ENDPOINT_CONTRACTS}/contratos/${id}/aditivos`,
                data
            )
    }
}

const getContractByQRCode = async ({
    hashVersao,
    id
}: PayloadQRCodeContract) => {
    return await api.get(
        `${ENDPOINT_CONTRACTS}/contratos/${id}/validacao?hash=${hashVersao}`
    )
}

const getContractByUF = async (uf: string, chassi: string) => {
    return await api.get(
        `${ENDPOINT_CONTRACTS}/consultas/gravames?uf=${uf}&chassi=${chassi}`
    )
}

const cancelContract = async ({ body, id }: ParamasChangeStatus) => {
    return api.post(`${ENDPOINT_CONTRACTS}/contratos/${id}/baixa`, { ...body })
}

const generateTxtReasonReject = async (
    id?: string
): Promise<AxiosResponse<string>> => {
    return await api.get(
        `${ENDPOINT_CONTRACTS}/contratos/operacoes/${id}/transacoes`
    )
}

const getContracts = async (
    query?: ContractsQueryDTO,
    isBackoffice?: boolean,
    IF?: string
): Promise<AxiosResponse<ContractsResponseDTO[]>> => {
    if (!isBackoffice) {
        return await api.get(
            `${ENDPOINT_CONTRACTS.replace(
                'v1',
                'v2'
            )}/contratos?${new URLSearchParams(Object.assign(query ?? {}))}`
        )
    } else {
        return await api.get(
            `${ENDPOINT_API_INSTITUTIONS}/instituicoes-financeiras/${IF}/contratos?${new URLSearchParams(
                Object.assign(query ?? {})
            )}`
        )
    }
}

const getContract = async (
    id?: string
): Promise<AxiosResponse<ContractResponseUpdateDTO>> => {
    return await api.get(`${ENDPOINT_CONTRACTS}/contratos/${id}`)
}
const getContractType = async (id: string): Promise<AxiosResponse<any>> => {
    return await api.get(`${ENDPOINT_CONTRACTS}/contratos/${id}/tipo-operacoes`)
}

const getLote = async (
    query?: LoteQueryDTO,
    isBackoffice?: boolean,
    IF?: string,
    indexTab?: number
): Promise<AxiosResponse<LoteResponseDTO[]>> => {
    const isImage = indexTab === 2
    if (!isBackoffice) {
        return await api.get(
            `${ENDPOINT_CONTRACTS}/lotes${isImage ? '/imagens' : ''
            }?${new URLSearchParams(Object.assign(query ?? {}))}`
        )
    } else {
        return await api.get(
            `${ENDPOINT_API_INSTITUTIONS}/instituicoes-financeiras/${IF}/lotes${isImage ? '/imagens' : ''
            }?${new URLSearchParams(Object.assign(query ?? {}))}`
        )
    }
}

const createContract = async (
    data: ContractRequestDTO
): Promise<AxiosResponse<FinalResponse>> => {
    return await api.post(`${ENDPOINT_CONTRACTS}/contratos`, data, {
        headers: {
            Authorization: `Bearer ${sessionStorage.getItem('idToken')}`
        }
    })
}

const createExternalAdditive = async (
    data: ExternalAdditiveRequestDTO
): Promise<AxiosResponse<FinalResponse>> => {
    return await api.post(
        `${ENDPOINT_CONTRACTS}/contratos/externos/aditivos`,
        data,
        {
            headers: {
                Authorization: `Bearer ${sessionStorage.getItem('idToken')}`
            }
        }
    )
}

const forwardToDetran = async (
    { url, transacao }: FinalResponse,
    config?: AxiosRequestConfig<any>
): Promise<AxiosResponse<void>> => {
    const dataTransacao = JSON.parse(transacao)
    const data = Object.keys(dataTransacao)

    const typeBoolean =
        typeof dataTransacao[data[0]] === 'object' ? true : false

    const restTransoform = Object.fromEntries(
        Object.keys(dataTransacao).map(el => [
            `${el}`,
            typeBoolean ? JSON.stringify(dataTransacao[el]) : dataTransacao[el]
        ])
    )

    return await api.post(
        `${url}`,
        { ...restTransoform },
        Object.assign(config ?? {})
    )
}

const postAnnex = async (
    id: string,
    data: ContractAnnexRequestDTO
): Promise<AxiosResponse<any>> => {
    return await api.post(
        `${ENDPOINT_CONTRACTS}/contratos/${id}/anexos`,
        data,
        {
            headers: {
                Authorization: `Bearer ${sessionStorage.getItem('idToken')}`
            }
        }
    )
}

const postBatch = async (
    data: FormData,
    indexTab?: number
): Promise<AxiosResponse<any>> => {
    const isImage = indexTab === 2
    return await api.post(
        `${ENDPOINT_CONTRACTS}/lotes${isImage ? '/imagens/' : '/'}`,
        data,
        {
            headers: {
                Authorization: `Bearer ${sessionStorage.getItem('idToken')}`,
                'Content-Type': 'multipart/form-data'
            }
        }
    )
}

const deleteAnnex = async (
    id: string,
    idAnnex: string
): Promise<AxiosResponse<void>> => {
    return await api.delete(
        `${ENDPOINT_CONTRACTS}/contratos/${id}/anexos/${idAnnex}`
    )
}

const downloadAnnex = async (
    id: string,
    idAnnex: string
): Promise<AxiosResponse<BlobPart>> => {
    return await api.get(
        `${ENDPOINT_CONTRACTS}/contratos/${id}/anexos/${idAnnex}`,
        {
            headers: {
                Authorization: `Bearer ${sessionStorage.getItem('idToken')}`
            }
        }
    )
}

const getLoteFile = async (
    id: string,
    typeFile: 'DOWNLOAD' | 'PREVIEW',
    indexTab?: number
): Promise<AxiosResponse<BlobPart>> => {
    const isImage = indexTab === 2

    const query =
        typeFile === 'DOWNLOAD' ? { tipo: 'retorno' } : { tipo: 'origem' }
    return await api.get(
        `${ENDPOINT_CONTRACTS}/lotes${isImage ? '/imagens/' : '/'
        }${id}/downloads?${new URLSearchParams(Object.assign(query ?? {}))}`
    )
}

const getHistoricByContractID = (id: string) => {
    return api.get(`${ENDPOINT_CONTRACTS}/contratos/${id}/historicos`)
}

const generateContractHistoricFile = (id: string) => {
    return api.get(
        `${ENDPOINT_CONTRACTS}/contratos/${id}/historicos/auditorias`
    )
}

const getReasonRefusedContract = (id: string) => {
    return api.get(
        `${ENDPOINT_CONTRACTS}/contratos/operacoes/${id}/mensagem-retorno`
    )
}

const preparsignatureContact = async (
    id: string,
    arquivo: File): Promise<AxiosResponse<any>> => {
    const formData = new FormData();
    formData.append('pdf', arquivo);

    return await api.post(
        `${ENDPOINT_CONTRACTS}/contratos/${id}/anexos/preparar`,
        formData,
        {
            headers: {
                Authorization: `Bearer ${sessionStorage.getItem('idToken')}`,
                'Content-Type': 'multipart/form-data'
            }
        }
    );
};

const finalizeSignatureContact = async (
    id: string,
    documentoId: string,
    assinaturaBase64: string
): Promise<AxiosResponse<any>> => {
    return await api.post(
        `${ENDPOINT_CONTRACTS}/contratos/${id}/anexos/finalizar`,
        {
            documentoId,
            assinaturaBase64
        },
        {
            headers: {
                Authorization: `Bearer ${sessionStorage.getItem('idToken')}`
            }
        }
    )
}

export const exportContracts = async (
    params: ExportContractsParams
): Promise<AxiosResponse<Blob>> => {
    return api.get(`${ENDPOINT_CONTRACTS_V2}/contratos/exportar`, {
        params
    })
}

const getContractsByQuery = async (
    params: ContractSearchRequestDTO
): Promise<AxiosResponse<ContractSearchResponseDTO[]>> => {
    const baseConsulta = params.baseConsulta?.trim().toUpperCase()

    if (baseConsulta === 'SNG') {
        return await api.get<ContractSearchResponseDTO[]>(
            `${ENDPOINT_API_DETRANS}/detrans/es/contratos/sng`,
            {
                params: removeEmptyValues({
                    chassi: params.chassi?.trim(),
                    cnpjAgenteFinanceiro:
                        params.cnpjAgenteFinanceiro?.replace(/\D/g, ''),
                    numeroContrato: params.numeroContrato?.trim(),
                    numeroAditivo: params.numeroAditivo?.trim()
                })
            }
        )
    }

    const { baseConsulta: _baseConsulta, ...paramsDetran } = params

    return await api.get<ContractSearchResponseDTO[]>(
        `${ENDPOINT_CONTRACTS}/consultas/contratos`,
        {
            params: removeEmptyValues({
                ...paramsDetran,
                cnpjAgenteFinanceiro:
                    paramsDetran.cnpjAgenteFinanceiro?.replace(/\D/g, ''),
                cpfCnpjDevedor:
                    paramsDetran.cpfCnpjDevedor?.replace(/\D/g, ''),
                chassi: paramsDetran.chassi?.trim(),
                numeroContrato: paramsDetran.numeroContrato?.trim(),
                numeroAditivo: paramsDetran.numeroAditivo?.trim()
            })
        }
    )
}

const getHistory = async (
    params: HistorySearchRequestDTO,
    instituicaoFinanceiraId?: string
): Promise<AxiosResponse<HistorySearchResponseDTO[]>> => {
    const { pagina, limite, ...otherFilters } = params;

    const queryParams = {
        pagina: pagina || 1,
        limite: limite || 10,
        ...otherFilters
    };

    if (instituicaoFinanceiraId) {
        queryParams.intituicaoFinanceira = instituicaoFinanceiraId;
    }

    return await api.get<HistorySearchResponseDTO[]>(
        `${ENDPOINT_CONTRACTS}/consultas/historicos`,
        { params: queryParams }
    );
};

const getVehicles = async (
    params: QueryVehicles,
): Promise<AxiosResponse<QueryVehicles[]>> => {
    const { pagina, limite, ...otherFilters } = params;

    const queryParams = {
        pagina: pagina || 1,
        limite: limite || 10,
        ...otherFilters
    };

    return await api.get<QueryVehicles[]>(
        `${ENDPOINT_CONTRACTS}/veiculos/marcas-modelos`,
        { params: queryParams }
    );
};

const createVehicle = async (
    data: { marca: string; modelo: string; ativo: boolean }
): Promise<AxiosResponse<any>> => {
    return await api.post(
        `${ENDPOINT_CONTRACTS}/veiculos/marcas-modelos`,
        data,
        {
            headers: {
                Authorization: `Bearer ${sessionStorage.getItem('idToken')}`
            }
        }
    );
};

const updateVehicle = async (
    guid: string,
    data: { marca: string; modelo: string; ativo: boolean }
): Promise<AxiosResponse<any>> => {
    return await api.put(
        `${ENDPOINT_CONTRACTS}/veiculos/marcas-modelos/${guid}`,
        data,
        {
            headers: {
                Authorization: `Bearer ${sessionStorage.getItem('idToken')}`
            }
        }
    );
};

const deleteVehicle = async (guid: string): Promise<AxiosResponse<void>> => {
    return await api.delete(
        `${ENDPOINT_CONTRACTS}/veiculos/marcas-modelos/${guid}`,
        {
            headers: {
                Authorization: `Bearer ${sessionStorage.getItem('idToken')}`
            }
        }
    );
};

export interface ContractFilesQueryDTO {
    ufContrato?: string
    cnpjAgenteFinanceiro?: string
    numeroContrato?: string
    numeroAditivo?: string
    chassi?: string
}

export interface ContractFileListItemDTO {
    index: string
    nome: string
    link?: string
    dataRegistro?: string
}

const getContractFiles = async (
    params: ContractFilesQueryDTO
): Promise<AxiosResponse<ContractFileListItemDTO[]>> => {
    if (params.ufContrato === 'ES') {
        return api.get<ContractFileListItemDTO[]>(
            `${ENDPOINT_API_DETRANS}/detrans/es/contratos/arquivos`,
            {
                params: {
                    cnpjAgenteFinanceiro: params.cnpjAgenteFinanceiro,
                    numeroContrato: params.numeroContrato,
                    numeroAditivo: params.numeroAditivo,
                    chassi: params.chassi
                }
            }
        )
    }

    return api.get<ContractFileListItemDTO[]>(
        `${ENDPOINT_API_DETRANS}/detrans/pr/contratos/arquivos`,
        {
            params: {
                cnpjAgenteFinanceiro: params.cnpjAgenteFinanceiro,
                numeroContrato: params.numeroContrato
            }
        }
    )
}
const downloadContractFile = async (
    params: Required<Pick<ContractFilesQueryDTO, 'cnpjAgenteFinanceiro' | 'numeroContrato'>> & {
        index: string
    }
): Promise<AxiosResponse<Blob>> => {
    return await api.get(
        `${ENDPOINT_API_DETRANS}/detrans/pr/contratos/arquivos/download`,
        {
            params: {
                cnpjAgenteFinanceiro: params.cnpjAgenteFinanceiro,
                numeroContrato: params.numeroContrato,
                index: params.index
            },
            responseType: 'blob',
            headers: {
                Accept: '*/*'
            }
        }
    )
}

const ContractService = {
    editContract,
    getContracts,
    getContract,
    getContractType,
    createContract,
    createExternalAdditive,
    forwardToDetran,
    postAnnex,
    postBatch,
    deleteAnnex,
    downloadAnnex,
    getLote,
    getLoteFile,
    getContractByQRCode,
    cancelContract,
    getHistoricByContractID,
    getContractByUF,
    getReasonRefusedContract,
    preparsignatureContact,
    generateTxtReasonReject,
    exportContracts,
    generateContractHistoricFile,
    getContractsByQuery,
    getHistory,
    getVehicles,
    createVehicle,
    updateVehicle,
    deleteVehicle,
    finalizeSignatureContact,
    getContractFiles,
    downloadContractFile
}

export default ContractService
